import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../hooks/useTranslations";
import { LadderReinforcement } from "../../../../../../http/vertigrip/types/ladderReinforcements";
import { useLadderReinforcementsApi } from "../../../../../../http/vertigrip/useLadderReinforcements";
import { PaginationQuery } from "../../../../../../types";
import FlexContent from "../../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../ui/ModalDialog";
import SearchBar from "../../../../../ui/SearchBar";
import Table from "../../../../../ui/Table";
import Visibility from "../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import CreateLadderReinforcementModalDialogButton from "./CreateLadderReinforcementModalDialogButton";
import EditLadderReinforcementModalDialog from "./EditLadderReinforcementModalDialog";

type Props = {
  active?: boolean;
};

const LadderReinforcements: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "ladderReinforcements" });
  const aborter = useRef(new AbortController());
  const ladderReinforcementApi = useLadderReinforcementsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "ladderReinforcements");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await ladderReinforcementApi.updateAdminLadderReinforcementVisibility(id, { visibility }, { installationTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (ladderReinforcementId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await ladderReinforcementApi.updateAdminLadderReinforcementOrder(
        ladderReinforcementId,
        { order: newOrder },
        { installationTypeId },
      );
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<LadderReinforcement>();
  const [itemToDelete, setItemToDelete] = useState<LadderReinforcement>();
  const { accessesAction, accessesDialog } = useEntityAccesses<LadderReinforcement>("vertigrip.ladder_reinforcement");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = ladderReinforcementApi.useAdminInstallationTypeLadderReinforcements(
    installationTypeId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.ladderReinforcements.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.ladderReinforcements.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateLadderReinforcementModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<LadderReinforcement>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.id}`}>
                {item.code}
              </Link>
            </td>
            <td>{item.description}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.ladderReinforcements.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                ladderReinforcementApi
                  .deleteAdminLadderReinforcement(itemToDelete.id, itemToDelete.installationType.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.ladderReinforcements.questions.delete}
        </ModalDialog>
      )}
      <EditLadderReinforcementModalDialog onClose={() => setItemToEdit(undefined)} ladderReinforcement={itemToEdit} />
    </FlexContent>
  );
};

export default LadderReinforcements;
